import { Fragment } from "react";
import React, { useState, useEffect, useRef, useContext } from "react";
import AppContext from "../../Store/AppContext";
import ChatMessageList from "./ChatComponente/ChatMessageList";
import {
    ListarHistoricoMensagens,
    EnviarMensagem,
    EnviarMensagemComArquivo,
    MarcarMensagensComoLidasPorNumero,
    VerificarSeCanalConversaEstaAberto,
} from "../../Services/WhatsApp";
import "react-chat-elements/dist/main.css";
import "./WhatsAppChat.css";
import MensagemRespondidaPreview from "./ChatComponente/MensagemRespondidaPreview";
import ChatTemplateSender from "./ChatComponente/ChatTemplateSender";
import ChatEtiquetaManager, { ConsultarEtiquetasDoContato } from "./ChatComponente/ChatEtiquetaManager";

export function CelularValido(ddd, numero) {
    // Remove qualquer coisa que não seja número
    ddd = String(ddd).replace(/\D/g, "");
    numero = String(numero).replace(/\D/g, "");

    // Valida DDD (2 dígitos entre 11 e 99)
    const dddValido = /^[1-9][0-9]$/.test(ddd);

    // Valida número de celular brasileiro (9 dígitos e começando com 9)
    const numeroValido = /^9\d{8}$/.test(numero);

    return dddValido && numeroValido;
}

export default function WhatsAppChat({
    operadorId,
    DesabilitarRecusosAutomaticos = false,
    DddCelular,
    NumCelular
}) {
    const { contaWhatsAppDefault, session } = useContext(AppContext);
    const operadorContextoId = operadorId ?? session.usuarioId;

    const [inputText, setInputText] = useState("");
    const [messages, setMessages] = useState([]);
    const [mensagemParaResponder, setMensagemParaResponder] = useState(null);

    const [gravando, setGravando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioURL, setAudioURL] = useState(null);
    const [modalTemplateAberto, setModalTemplateAberto] = useState(false);
    const [canalConversaEstaAberto, setCanalConversaEstaAberto] = useState(null);
    const [modalEtiquetasAberto, setModalEtiquetasAberto] = useState(false);
    const [etiquetaIdsContato, setEtiquetaIdsContato] = useState([]);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const mimeTypeRef = useRef("audio/ogg");
    const fileInputRef = useRef(null);
    const streamRef = useRef(null);
    const numeroDestino = `55${DddCelular}${NumCelular}`;
    const automacoesInativas = DesabilitarRecusosAutomaticos || modalTemplateAberto || modalEtiquetasAberto;

    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    const ultimaMensagemIdRef = useRef(null);

    useEffect(() => {
        scrollAndFocus();
    }, [messages]);

    useEffect(() => {
        scrollAndFocus();
    }, [mensagemParaResponder]);

    useEffect(() => {
        return () => {
            if (
                mediaRecorderRef.current &&
                mediaRecorderRef.current.state !== "inactive"
            ) {
                mediaRecorderRef.current.stop();
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const marcarComoLidoNaAbertura = async () => {
            if (!DddCelular || !NumCelular) return;

            // 1️⃣ Buscar histórico inicial
            const response = await ListarHistoricoMensagens(
                contaWhatsAppDefault,
                DddCelular,
                NumCelular,
            );
            if (!response.success) return;

            const mensagens = response.mensagens || [];
            setMessages(mensagens);

            const ultima = mensagens[mensagens.length - 1];
            ultimaMensagemIdRef.current =
                ultima?.logConversasWhatsAppCodigoExterno || null;

            // 2️⃣ Verificar se existe mensagem NÃO lida
            const existeNaoLida = mensagens.some(
                (m) =>
                    m.metaWhatsAppMensagemLido === 0 ||
                    m.metaWhatsAppMensagemLido === false ||
                    m.metaWhatsAppMensagemLido == null, // trata null como não lida
            );

            // 3️⃣ Se houver, marcar todas como lidas uma única vez
            if (existeNaoLida) {
                await MarcarMensagensComoLidasPorNumero(
                    contaWhatsAppDefault,
                    "55",
                    DddCelular,
                    NumCelular,
                );
            }

            await atualizarStatusCanal();

            // 4️⃣ Scroll para baixo no carregamento
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: "auto" });
            }
        };

        marcarComoLidoNaAbertura();
        setMensagemParaResponder(null);
    }, [DddCelular, NumCelular]);

    useEffect(() => {
        if (automacoesInativas) return;

        const interval = setInterval(async () => {
            if (!DddCelular || !NumCelular) return;

            const response = await ListarHistoricoMensagens(
                contaWhatsAppDefault,
                DddCelular,
                NumCelular,
            );
            if (!response.success) return;

            const novasMensagens = response.mensagens;
            if (!novasMensagens?.length) return;

            const ultimaNova = novasMensagens[novasMensagens.length - 1];
            const novaUltimaId = ultimaNova?.logConversasWhatsAppCodigoExterno;

            if (
                !ultimaMensagemIdRef.current ||
                novaUltimaId !== ultimaMensagemIdRef.current
            ) {
                ultimaMensagemIdRef.current = novaUltimaId;

                setMessages(novasMensagens);
                await atualizarStatusCanal();

                // 2️⃣ Verificar se existe mensagem NÃO lida
                const existeNaoLida = novasMensagens.some(
                    (m) =>
                        m.metaWhatsAppMensagemLido === 0 ||
                        m.metaWhatsAppMensagemLido === false ||
                        m.metaWhatsAppMensagemLido == null, // trata null como não lida
                );

                // 3️⃣ Se houver, marcar todas como lidas uma única vez
                if (existeNaoLida) {
                    await MarcarMensagensComoLidasPorNumero(
                        contaWhatsAppDefault,
                        "55",
                        DddCelular,
                        NumCelular,
                    );
                }
            }

            scroll("auto"); // se você tiver scroll com behavior, use auto aqui
        }, 5000);

        return () => clearInterval(interval);
    }, [DddCelular, NumCelular, contaWhatsAppDefault, automacoesInativas]);

    useEffect(() => {
        const run = async () => {
            if (!session?.usuarioId) return;
            if (!numeroDestino) return;

            const resp = await ConsultarEtiquetasDoContato({
                usuarioId: session.usuarioId,
                whatsAppId: numeroDestino
            });

            if (resp.success) setEtiquetaIdsContato(resp.etiquetaIds || []);
            else setEtiquetaIdsContato([]);
        };

        run();
    }, [session?.usuarioId, numeroDestino]);

    const scroll = () => {
        if (automacoesInativas) return;

        const el = bottomRef.current?.parentElement; // chat-messages / chat-messages-mono
        if (!el) return;

        el.scrollTop = el.scrollHeight;
    };

    const Focus = () => {
        if (automacoesInativas) return;

        // Focar no input de texto
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };



    const scrollAndFocus = () => {
        scroll();
        Focus();
    };

    const atualizarStatusCanal = async () => {
        if (!DddCelular || !NumCelular) {
            setCanalConversaEstaAberto(false);
            return;
        }

        const { success, result } = await VerificarSeCanalConversaEstaAberto(
            contaWhatsAppDefault,
            "55",
            DddCelular,
            NumCelular,
        );

        setCanalConversaEstaAberto(success ? result.resultData : false);
    };

    const handleSend = async () => {
        setEnviando(true);
        if (inputText.trim()) {
            const { success, result, message } = await EnviarMensagem(
                contaWhatsAppDefault,
                numeroDestino,
                inputText,
                mensagemParaResponder
                    ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                    : null,
            );

            if (success) {
                setMessages([
                    ...messages,
                    {
                        logConversasWhatsAppDirecao: "sent",
                        type: "text",
                        logConversasWhatsAppMensagem: inputText,
                        logConversasWhatsAppCodigoExterno: mensagemParaResponder
                            ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                            : null,
                    },
                ]);
                setInputText("");

                scrollAndFocus();
            } else {
                setEnviando(false);
                setMensagemParaResponder(null);
                alert("Erro ao enviar mensagem: " + (message || "Erro desconhecido"));
            }
        }
        setEnviando(false);
        setMensagemParaResponder(null);
    };

    const recarregarHistorico = async () => {
        if (!DddCelular || !NumCelular) return;

        const response = await ListarHistoricoMensagens(
            contaWhatsAppDefault,
            DddCelular,
            NumCelular,
        );
        if (!response.success) return;

        const mensagens = response.mensagens || [];
        setMessages(mensagens);

        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const iniciarGravacao = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Tenta usar OGG/Opus primeiro
            let mimeType = "audio/ogg;codecs=opus";
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                // Fallback para webm
                mimeType = "audio/webm;codecs=opus";
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    alert(
                        "Seu navegador não suporta nenhum formato de áudio compatível.",
                    );
                    return;
                }
            }

            mimeTypeRef.current = mimeType;

            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                processarAudioGravado();

                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((track) => track.stop());
                    streamRef.current = null;
                }
            };

            mediaRecorderRef.current.start();
            setGravando(true);
        } catch (error) {
            console.error("Erro ao acessar o microfone:", error);
            alert("Erro ao acessar o microfone. Verifique permissões.");
        }
    };

    // Finaliza gravação
    const pararGravacao = () => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== "inactive"
        ) {
            mediaRecorderRef.current.stop();
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        setGravando(false);
    };

    // Processa áudio gravado
    const processarAudioGravado = () => {
        const mimeType = mimeTypeRef.current || "audio/ogg";
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
    };

    const enviarAudio = async () => {
        if (!audioBlob) return;
        setEnviando(true);

        try {
            const extension = mimeTypeRef.current.includes("webm") ? "webm" : "ogg";
            const filename = `audio.${extension}`;
            const file = new File([audioBlob], filename, {
                type: mimeTypeRef.current,
            });

            const { success, message } = await EnviarMensagemComArquivo(
                contaWhatsAppDefault,
                numeroDestino,
                mensagemParaResponder
                    ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                    : null,
                file,
                "audio",
            );

            if (success) {
                setMessages([
                    ...messages,
                    {
                        logConversasWhatsAppDirecao: "sent",
                        type: "audio",
                        logConversasWhatsAppMensagem: "[Áudio enviado]",
                        logConversasWhatsAppCodigoExterno: mensagemParaResponder
                            ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                            : null,
                    },
                ]);
                setAudioBlob(null);
                setAudioURL(null);
                setMensagemParaResponder(null);
            } else {
                setEnviando(false);
                setMensagemParaResponder(null);
                alert("Erro ao enviar áudio: " + (message || "Erro desconhecido"));
            }
        } catch (error) {
            setEnviando(false);
            setMensagemParaResponder(null);
            console.error("Erro ao enviar áudio:", error);
            alert("Erro ao enviar áudio.");
        }
        setEnviando(false);
        setMensagemParaResponder(null);
    };

    const cancelarAudio = () => {
        setAudioBlob(null);
        setAudioURL(null);
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const mimeType = file.type;
        const fileName = file.name.toLowerCase();

        // Restrições
        const extensoesBloqueadas = [
            ".exe",
            ".bat",
            ".cmd",
            ".js",
            ".apk",
            ".zip",
            ".rar",
        ];
        const maxFileSize = 10 * 1024 * 1024; // 10MB

        if (extensoesBloqueadas.some((ext) => fileName.endsWith(ext))) {
            alert("Este tipo de arquivo não é permitido.");
            return;
        }

        if (file.size > maxFileSize) {
            alert("O arquivo é muito grande. Tamanho máximo permitido: 10MB.");
            return;
        }

        if (mimeType.startsWith("audio/")) {
            alert("Envio de áudios só é permitido via gravação.");
            return;
        }

        // Classificação final do tipo de mídia
        const tipoMidia = mimeType.startsWith("image/") ? "image" : "document";

        try {
            const { success, message } = await EnviarMensagemComArquivo(
                contaWhatsAppDefault,
                numeroDestino,
                mensagemParaResponder
                    ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                    : null,
                file,
                tipoMidia,
            );

            if (success) {
                setMessages((prev) => [
                    ...prev,
                    {
                        logConversasWhatsAppDirecao: "sent",
                        type: tipoMidia,
                        logConversasWhatsAppMensagem: `[${tipoMidia === "image" ? "Imagem" : "Documento"} enviado: ${file.name}]`,
                        logConversasWhatsAppCodigoExterno: mensagemParaResponder
                            ? mensagemParaResponder.logConversasWhatsAppCodigoExterno
                            : null,
                    },
                ]);
                setMensagemParaResponder(null);
            } else {
                alert("Erro ao enviar arquivo: " + (message || "Erro desconhecido"));
            }
        } catch (error) {
            console.error("Erro ao enviar arquivo:", error);
            alert("Erro ao enviar arquivo.");
        } finally {
            event.target.value = "";
        }
        setMensagemParaResponder(null);
    };

    const handleResponderMensagem = (mensagem) => {
        setMensagemParaResponder(mensagem);
    };

    return (
        <div className="chat-container">
            <div className="chat-messages">
                <ChatMessageList
                    mensagens={messages}
                    onResponderMensagem={handleResponderMensagem}
                    conversaKey={`${numeroDestino}`}
                />
                <div ref={bottomRef} />
            </div>

            <div className="chat-controls">
                {mensagemParaResponder && (
                    <div className="reply-preview-container">
                        Respondendo:
                        <MensagemRespondidaPreview mensagem={mensagemParaResponder} />
                        <button
                            type="button"
                            className="reply-preview-close"
                            onClick={() => setMensagemParaResponder(null)}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {audioBlob && (
                    <div className="chat-input-container audio-preview">
                        <audio className="chat-text-input" controls src={audioURL}></audio>
                        <button
                            className="chat-cancel-button"
                            onClick={cancelarAudio}
                            title="Cancelar gravação"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="24"
                                viewBox="0 0 24 24"
                                width="24"
                                fill="#fff"
                            >
                                <path d="M0 0h24v24H0V0z" fill="none" />
                                <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-4.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z" />
                            </svg>
                        </button>
                        <button
                            className="chat-send-button"
                            onClick={enviando ? null : enviarAudio}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="24"
                                viewBox="0 0 24 24"
                                width="24"
                                fill="#fff"
                            >
                                <path d="M0 0h24v24H0z" fill="none" />
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                        </button>
                    </div>
                )}

                {!audioBlob && (
                    <div className="chat-input-container">
                        {gravando ? (
                            <div className="chat-label-gravando"> 🛑 Gravando... </div>
                        ) : (
                            <Fragment>
                                <div className="chat-etiqueta-wrapper">
                                    <button
                                        type="button"
                                        className={
                                            canalConversaEstaAberto
                                                ? "chat-etiqueta-button"
                                                : "chat-etiqueta-button-canal-fechado"
                                        }
                                        onClick={() => setModalEtiquetasAberto(true)}
                                        title={
                                            (etiquetaIdsContato?.length || 0) > 0
                                                ? `Este contato tem ${etiquetaIdsContato.length} etiqueta(s)`
                                                : "Gerenciar etiquetas"
                                        }
                                        aria-label="Gerenciar etiquetas"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="23"
                                            height="23"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#005848"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M20 12V7a2 2 0 0 0-2-2h-5L4 14l6 6 9-9z" />
                                            <circle cx="16.5" cy="7.5" r="1.5" />
                                        </svg>
                                    </button>

                                    {(etiquetaIdsContato?.length || 0) > 0 && (
                                        <span className="chat-etiqueta-badge" aria-hidden="true">
                                            {etiquetaIdsContato.length > 99 ? "99+" : etiquetaIdsContato.length}
                                        </span>
                                    )}
                                </div>

                                {!canalConversaEstaAberto && (
                                    <div className="chat-banner-canal-fechado">
                                        Canal de conversa fechado: janela de 24h expirada ou a
                                        conversa não foi iniciada. Você só pode enviar mensagem via
                                        <b>template</b>.
                                    </div>
                                )}


                                {canalConversaEstaAberto && (
                                    <input
                                        type="text"
                                        className="chat-text-input"
                                        disabled={enviando}
                                        placeholder="Digite uma mensagem..."
                                        value={inputText}
                                        ref={inputRef}
                                        onChange={(e) => setInputText(e.target.value)}
                                        onKeyDown={
                                            enviando ? null : (e) => e.key === "Enter" && handleSend()
                                        }
                                    />
                                )}




                                <button
                                    type="button"
                                    className={
                                        canalConversaEstaAberto
                                            ? "chat-template-button"
                                            : "chat-template-button-canal-fechado"
                                    }
                                    onClick={() => setModalTemplateAberto(true)}
                                    title="Enviar template"
                                    aria-label="Enviar template"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#005848"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <line x1="10" y1="9" x2="8" y2="9" />
                                    </svg>
                                </button>

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    style={{ display: "none" }}
                                    onChange={handleFileSelect}
                                />
                                {canalConversaEstaAberto && (
                                    <button
                                        onClick={
                                            !canalConversaEstaAberto
                                                ? null
                                                : () => fileInputRef.current?.click()
                                        }
                                        title="Enviar arquivo"
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            padding: 0,
                                            cursor: "pointer",
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#005848"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.19a3 3 0 014.24 4.24L8.46 18.07" />
                                        </svg>
                                    </button>
                                )}
                            </Fragment>
                        )}

                        {canalConversaEstaAberto && (
                            <button
                                className="chat-mic-button"
                                onClick={
                                    !canalConversaEstaAberto
                                        ? null
                                        : gravando
                                            ? pararGravacao
                                            : iniciarGravacao
                                }
                                disabled={!canalConversaEstaAberto}
                                title={gravando ? "Parar gravação" : "Gravar áudio"}
                            >
                                {gravando ? "⏹️" : "🎤"}
                            </button>
                        )}
                        {gravando || !canalConversaEstaAberto ? null : (
                            <button
                                className="chat-send-button"
                                onClick={enviando ? null : handleSend}
                                disabled={enviando}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    width="24"
                                    fill="#fff"
                                >
                                    <path d="M0 0h24v24H0z" fill="none" />
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>
            <ChatTemplateSender
                aberto={modalTemplateAberto}
                onFechar={() => setModalTemplateAberto(false)}
                contaMeta={contaWhatsAppDefault}
                numeroDestino={numeroDestino}
                onEnviadoSucesso={async () => {
                    setModalTemplateAberto(false);
                    await recarregarHistorico();
                    setMensagemParaResponder(null);
                }}
            />
            <ChatEtiquetaManager
                aberto={modalEtiquetasAberto}
                onFechar={() => setModalEtiquetasAberto(false)}

                usuarioId={operadorContextoId}
                whatsAppId={numeroDestino}
                onSalvoSucesso={({ etiquetaIds }) => {
                    setEtiquetaIdsContato(etiquetaIds || []);
                    setModalEtiquetasAberto(false);
                }}
            />
        </div>
    );
}
